import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  DateRangePicker,
  computePreset,
  type DatePreset,
  type DateRange,
} from '@/components/DateRangePicker';

export const Route = createFileRoute('/_authed/analytics/suggestions')({
  component: SuggestionsPage,
});

const PAGE_SIZE = 50;

// Drill-down page from the dashboard — reads every suggestion within the
// date window, with optional per-POI filter. We render the text in full
// (no truncation) because visitor suggestions are short by design (max
// 4000 chars; almost always <300 in practice) and the admin needs to
// read every word to act on them.

function SuggestionsPage() {
  const [preset, setPreset] = useState<DatePreset>('30d');
  const [range, setRange] = useState<DateRange>(() => computePreset('30d'));
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [page, setPage] = useState(0);

  // Cache the locations once for the dropdown. We pull from the funnel
  // endpoint because it already returns ordered POIs per route — saves
  // wiring a separate /admin/locations endpoint into this page.
  const funnelQ = useQuery({
    queryKey: ['analytics', 'funnel', { all: true }],
    queryFn: () => api.analyticsFunnel({}),
  });
  const locationOptions = useMemo(() => {
    if (!funnelQ.data) return [];
    const out: { id: string; label: string }[] = [];
    for (const r of funnelQ.data.routes) {
      for (const l of r.locations) {
        out.push({ id: l.id, label: `${l.position}. ${l.title.en}` });
      }
    }
    return out;
  }, [funnelQ.data]);

  const args = useMemo(
    () => ({
      ...range,
      locationId: locationFilter || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [range, locationFilter, page],
  );

  const listQ = useQuery({
    queryKey: ['analytics', 'suggestions', args],
    queryFn: () => api.analyticsSuggestions(args),
  });

  return (
    <div className="space-y-6">
      <div>
        <Link to="/analytics" className="btn-ghost -ml-3 mb-2">
          ← Back to analytics
        </Link>
        <h1 className="text-h1">Visitor suggestions</h1>
        <p className="mt-1 text-body text-slate-gray">
          Qualitative feedback from visitors. Filter by POI to focus on a
          specific stop, or by date range to follow up on a particular
          period.
        </p>
      </div>

      <DateRangePicker
        value={range}
        onChange={(v) => {
          setPage(0);
          setRange(v);
        }}
        preset={preset}
        onPresetChange={setPreset}
      />

      <div className="card flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[12rem]">
          <label className="label">POI</label>
          <select
            className="input"
            value={locationFilter}
            onChange={(e) => {
              setPage(0);
              setLocationFilter(e.target.value);
            }}
          >
            <option value="">All POIs</option>
            {locationOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <a
          href={api.csvUrl('suggestions', range)}
          className="btn-secondary"
          download
        >
          ⬇ Download CSV
        </a>
      </div>

      {listQ.isLoading ? (
        <div className="card text-slate-gray">Loading suggestions…</div>
      ) : listQ.data && listQ.data.suggestions.length > 0 ? (
        <div className="space-y-3">
          {listQ.data.suggestions.map((s) => (
            <article key={s.id} className="card">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-caption text-slate-gray">
                <span>
                  {s.location ? (
                    <span className="rounded-full bg-tech-blue/10 px-2 py-0.5 font-semibold text-tech-blue">
                      {s.location.title.en}
                    </span>
                  ) : (
                    <span className="rounded-full bg-light-gray px-2 py-0.5">
                      Route-level
                    </span>
                  )}
                </span>
                <span>
                  {formatLocal(s.createdAt)} ·{' '}
                  <span className="font-mono">{s.sessionId.slice(0, 8)}</span>
                </span>
              </div>
              <p className="whitespace-pre-wrap text-body text-dark-navy">
                {s.text}
              </p>
            </article>
          ))}
        </div>
      ) : (
        <div className="card text-slate-gray">No suggestions in this window.</div>
      )}

      {listQ.data && listQ.data.total > 0 ? (
        <div className="flex items-center justify-between text-caption text-slate-gray">
          <span>
            Showing {page * PAGE_SIZE + 1}-
            {Math.min(listQ.data.total, page * PAGE_SIZE + listQ.data.suggestions.length)}{' '}
            of {listQ.data.total}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="btn-ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </button>
            <button
              className="btn-ghost"
              disabled={(page + 1) * PAGE_SIZE >= listQ.data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatLocal(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
